// Pure helpers of the company classification admin page (tested in __tests__).

export const COMPANY_TYPE_LABELS: Record<string, string> = {
  esn: "ESN / Services informatiques",
  consulting: "Cabinet de conseil",
  software_tech: "Éditeur de logiciels / Tech",
  industry: "Industrie, BTP & énergie",
  retail: "Commerce & distribution",
  banking_insurance: "Banque & assurance",
  public_sector: "Secteur public, enseignement & recherche",
  healthcare: "Santé & pharma",
  telecom_media: "Télécoms, médias & communication",
  transport_logistics: "Transport & logistique",
  staffing_hr: "Recrutement & RH",
  other: "Autre",
};

export const COMPANY_TYPE_SOURCE_LABELS: Record<string, string> = {
  override: "Correction manuelle",
  signals: "Signaux métier",
  industry: "Industrie LinkedIn",
  unmapped: "Industrie non mappée",
};

/** Radix Select forbids empty values: sentinels for "no filter" / "default". */
export const ALL = "__all";
export const NONE = "__none";
export const DEFAULT = "__default";

export function companyTypeLabel(value: string | null | undefined): string {
  if (!value) return "Non classé";
  return COMPANY_TYPE_LABELS[value] ?? value;
}

export function companySourceLabel(value: string | null | undefined): string {
  if (!value) return "—";
  return COMPANY_TYPE_SOURCE_LABELS[value] ?? value;
}

/** Aliases typed one per line or comma-separated; trimmed, de-duplicated. */
export function parseAliases(text: string): string[] {
  const out: string[] = [];
  for (const part of text.split(/[\n,;]/)) {
    const alias = part.trim();
    if (alias && !out.includes(alias)) out.push(alias);
  }
  return out;
}

/** LinkedIn company ids: numbers or URNs (`urn:li:fsd_company:1033`), any separator. */
export function parseLinkedinIds(text: string): { ids: number[]; invalid: string[] } {
  const ids: number[] = [];
  const invalid: string[] = [];
  for (const part of text.split(/[\s,;]+/)) {
    const token = part.trim();
    if (!token) continue;
    const match = token.match(/(\d+)$/);
    const id = match ? Number(match[1]) : NaN;
    if (!Number.isSafeInteger(id) || id <= 0) {
      invalid.push(token);
    } else if (!ids.includes(id)) {
      ids.push(id);
    }
  }
  return { ids, invalid };
}

export function validateOverrideForm(form: { companyType: string; name: string; idsText: string }): string | null {
  if (!form.companyType) return "Choisissez un type d'entreprise";
  if (form.companyType === "other") return "« Autre » n'est pas un type de correction";
  const { ids, invalid } = parseLinkedinIds(form.idsText);
  if (invalid.length) return `Identifiants LinkedIn invalides : ${invalid.join(", ")}`;
  if (!form.name.trim() && ids.length === 0) return "Renseignez un nom ou au moins un identifiant LinkedIn";
  return null;
}

export function validateTiers(progressing: string, strong: string): string | null {
  const p = Number(progressing);
  const s = Number(strong);
  if (!Number.isInteger(p) || !Number.isInteger(s)) return "Les paliers doivent être des nombres entiers";
  if (p < 1 || p > 100 || s < 1 || s > 100) return "Les paliers doivent être compris entre 1 et 100";
  if (p >= s) return "« Progression » doit être strictement inférieur à « Forte progression »";
  return null;
}

/** Value of the industry override select for a row. */
export function industryOverrideValue(row: { override_type: string | null; override_untyped: boolean }): string {
  if (row.override_untyped) return NONE;
  return row.override_type ?? DEFAULT;
}

/** API payload for an industry override select value. */
export function industryOverridePayload(value: string): { override_type: string | null; override_untyped: boolean } {
  if (value === NONE) return { override_type: null, override_untyped: true };
  if (value === DEFAULT) return { override_type: null, override_untyped: false };
  return { override_type: value, override_untyped: false };
}

/** Tier a score bucket ("40-49") falls in, for colouring the distribution. */
export function bucketTier(bucket: string, progressingMin: number, strongMin: number): "below" | "progressing" | "strong" {
  const start = Number(bucket.split("-")[0]);
  if (start >= strongMin) return "strong";
  if (start >= progressingMin) return "progressing";
  return "below";
}

export function maxCount(distribution: { count: number }[]): number {
  return distribution.reduce((max, bucket) => Math.max(max, bucket.count), 0);
}
